/**
 * Shared JWT auto-refresh interceptor for Axios clients.
 *
 * Installs a response interceptor on the given Axios instance that:
 * - Catches 401 responses (except for auth endpoints themselves)
 * - Calls `/auth/refresh` on the core backend with the stored refresh token
 * - Stores the new tokens in localStorage
 * - Retries the original request with the new access token
 * - Queues concurrent failing requests during a refresh so they don't all fire
 *   separate refresh calls
 * - Falls back to `onRefreshFailed()` if the refresh itself fails (default:
 *   clear tokens + redirect to /login)
 *
 * Usage:
 * ```ts
 * import { installRefreshInterceptor } from '@bill/ui';
 *
 * installRefreshInterceptor({
 *   client: apiClient,       // bstock-backend or bstaff-backend
 *   coreApiUrl: 'https://core.example.fr/api',
 * });
 * // Optional: also install on coreApiClient for symmetry
 * installRefreshInterceptor({ client: coreApiClient, coreApiUrl: '...' });
 * ```
 */

import axios, {
  type AxiosInstance,
  type AxiosError,
  type InternalAxiosRequestConfig,
} from 'axios';

interface RetryableAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

type QueueItem = {
  resolve: (token: string | null) => void;
  reject: (error: unknown) => void;
};

// Shared state across all instrumented clients so that a single refresh in
// flight serves all pending 401s, regardless of which client triggered it.
let isRefreshing = false;
let failedQueue: QueueItem[] = [];

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
}

export interface InstallRefreshOptions {
  /** Axios client to instrument */
  client: AxiosInstance;
  /** Base URL of the core backend exposing /auth/refresh (e.g. "https://core.bill-app.fr/api") */
  coreApiUrl: string;
  /**
   * Callback invoked when the refresh itself fails (expired/invalidated refresh token).
   * Default: clear tokens from localStorage and redirect to /login.
   */
  onRefreshFailed?: () => void;
  /**
   * Optional request-interceptor that attaches the bearer token from localStorage.
   * Set to false if your client already installs a request interceptor (default: true).
   */
  installRequestInterceptor?: boolean;
}

function defaultOnRefreshFailed() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  if (typeof window !== 'undefined') {
    window.location.href = '/login';
  }
}

/**
 * Install the auto-refresh interceptor on an Axios client.
 * Idempotent: calling twice on the same client adds two interceptors — don't.
 */
export function installRefreshInterceptor(options: InstallRefreshOptions): void {
  const {
    client,
    coreApiUrl,
    onRefreshFailed = defaultOnRefreshFailed,
    installRequestInterceptor = true,
  } = options;

  // Request interceptor: attach bearer token from localStorage
  if (installRequestInterceptor) {
    client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  // Response interceptor: auto-refresh on 401
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as RetryableAxiosRequestConfig | undefined;

      // Permission denied (403) — let caller handle
      if (!originalRequest || error.response?.status !== 401) {
        return Promise.reject(error);
      }

      // Don't retry refresh for auth endpoints themselves
      const authEndpoints = ['/auth/login', '/auth/refresh'];
      if (authEndpoints.some((endpoint) => originalRequest.url?.includes(endpoint))) {
        return Promise.reject(error);
      }

      // Already retried once — give up
      if (originalRequest._retry) {
        return Promise.reject(error);
      }

      // A refresh is already in flight — queue this request
      if (isRefreshing) {
        return new Promise<string | null>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return client(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        // Direct axios call (not through the instrumented client) to avoid recursion
        const response = await axios.post<{
          data: { tokens: { accessToken: string; refreshToken: string } };
        }>(
          `${coreApiUrl}/auth/refresh`,
          { refreshToken },
          { headers: { 'Content-Type': 'application/json' } }
        );

        const {
          accessToken: newAccessToken,
          refreshToken: newRefreshToken,
        } = response.data.data.tokens;

        localStorage.setItem('accessToken', newAccessToken);
        localStorage.setItem('refreshToken', newRefreshToken);

        client.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        processQueue(null, newAccessToken);

        return client(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        onRefreshFailed();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );
}
